import React, { Component } from 'react';
import AFRAME from 'aframe';
import { withTranslation } from 'react-i18next';
import Slider from 'react-rangeslider';
import Button from '../../Buttons/Button';
import Icon from '../../Icon/Icons';
import { customGaEvent, PublicGaEvents } from '../../../utils/ga';
import './AudioPlayer.scss';

class AudioPlayer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      autoPlayStarted: false,
      playing: false,
      volumeLevel: 0.5,
      muted: false,
      progressBarWidth: props.width,
      currentTime: 0,
    };

    this.canvas = null;
    this.audio = null;
  }

  componentDidMount() {
    const { primaryColor, width, previewTour } = this.props;
    window.addEventListener(
      AFRAME.utils.device.isMobile() ? 'touchend' : 'click',
      () => !document.querySelector('.tutorial-modal.show') && this.autoPlay()
    );
    if (previewTour) {
      this.autoPlay();
    }
    this.audio = document.getElementById('audio');
    this.canvas = document.getElementById('progress-bar');
    this.audio.addEventListener('loadedmetadata', this.loadAudio);
    this.audio.addEventListener('play', this.setPlaying);
    this.audio.addEventListener('pause', this.setPlaying);
    this.audio.addEventListener('timeupdate', this.handleTimeUpdate);
    this.canvas.addEventListener('click', this.setAudioPosition);
    document.querySelector('.rangeslider__fill').style.background =
      primaryColor || '#009c66';

    if (!width) {
      const progressBarWidth =
        document.getElementById('progress-bar').offsetWidth - 10;
      this.setState({ progressBarWidth });
    }

  }

  componentDidUpdate(prevProps, prevState) {
    const { tracks, isMainMediaPlaying } = this.props;
    const { playing } = this.state;
    if (tracks[0].id !== prevProps.tracks[0].id) {
      this.resetProgressBar();
      this.setState(
        {
          autoPlayStarted: false,
          playing: false,
          showVolume: false,
          currentTime: 0,
        },
        () => {
          this.autoPlay();
        }
      );
    }

    if (
      playing &&
      isMainMediaPlaying !== prevProps.isMainMediaPlaying &&
      prevProps.isMainMediaPlaying
    ) {
      this.togglePlay();
    }
  }

  componentWillUnmount() {
    this.audio.removeEventListener('loadedemetadata', this.loadAudio);
    this.audio.removeEventListener('play', this.setPlaying);
    this.audio.removeEventListener('pause', this.setPlaying);
    this.audio.removeEventListener('timeupdate', this.updateTimeLabel);
    this.audio.removeEventListener('loadedmetadata', this.loadAudio);
    window.removeEventListener(
      AFRAME.utils.device.isMobile() ? 'touchend' : 'click',
      this.autoPlay
    );
    this.canvas.removeEventListener('click', this.setAudioPosition);
  }

  setPlaying = ({ type }) => {
    const playing = type === 'play';
    this.setState({ playing });
  };

  autoPlay = () => {
    const { toggleMainMediaStatus, autoPlay } = this.props;
    const { autoPlayStarted } = this.state;
    if (autoPlay && !autoPlayStarted) {
      this.togglePlay();
      if (typeof toggleMainMediaStatus === 'function') {
        toggleMainMediaStatus(true);
      }
      this.setState({ autoPlayStarted: true });
    }
  };

  loadAudio = () => {
    const { volumeLevel } = this.state;
    this.resetProgressBar();
    this.audio.volume = volumeLevel;
  };

  // assuming time is never greater than an hour
  formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time - minutes * 60);
    const minuteValue = minutes < 10 ? `0${minutes}` : minutes;
    const secondValue = seconds < 10 ? `0${seconds}` : seconds;

    return `${minuteValue}:${secondValue}`;
  }

  setAudioPosition = e => {
    const { progressBarWidth } = this.state;
    const { primaryColor = '#009c66' } = this.props;
    const xPosition = e.offsetX;
    const percentage = xPosition / progressBarWidth;

    if (percentage * this.audio.duration < this.audio.currentTime) {
      this.resetProgressBar();
    }
    this.audio.currentTime = percentage * this.audio.duration;
    this.canvas.getContext('2d').fillStyle = primaryColor;
    this.canvas
      .getContext('2d')
      .fillRect(0, 0, percentage * progressBarWidth, 5);
  };

  togglePlay = () => {
    const { displayIcon, toggleMainMediaStatus } = this.props;
    this.setState(
      prevState => ({ playing: !prevState.playing }),
      () => {
        const { playing } = this.state;
        const playState = playing ? 'Play' : 'Pause';
        displayIcon &&
          customGaEvent({
            action: PublicGaEvents.AUDIO,
            label: playState,
          });

        if (playing) {
          if (typeof toggleMainMediaStatus === 'function') {
            toggleMainMediaStatus(true);
          }
          try {
            this.audio.play();
          } catch (error) {
            console.log(error);
          }
        } else {
          if (typeof toggleMainMediaStatus === 'function') {
            toggleMainMediaStatus(false);
          }
          this.audio.pause();
        }
      }
    );
  };

  toggleMute = e => {
    const { muted } = this.state;
    if (e.target === this.audioCtrl) {
      if (muted) {
        this.audio.muted = false;
        this.setState({ muted: false, volumeLevel: this.audio.volume });
      } else {
        this.audio.muted = true;
        this.setState({ muted: true, volumeLevel: 0 });
      }
    }
  };

  updateBar = () => {
    const { playing, progressBarWidth } = this.state;
    const { primaryColor = '#009c66' } = this.props;

    if (playing) {
      this.resetProgressBar();
      const duration = this.audio.duration;
      const currentTime = this.audio.currentTime;
      const percentage = currentTime / duration;
      const progress = progressBarWidth * percentage;

      this.canvas.getContext('2d').fillStyle = primaryColor;
      this.canvas.getContext('2d').fillRect(0, 0, progress, 5);

      if (currentTime === duration) {
        this.resetProgressBar();
        this.setState({ playing: false });
      }
    }
  };

  changeVolume = val => {
    if (Boolean(val) && this.audio.muted) {
      this.audio.muted = false;
    }
    this.audio.volume = val;
    this.setState({ volumeLevel: this.audio.volume });
  };

  slideStart = () => {
    const slider = document.querySelector('.slider-wrap');
    slider.classList.add('active');
  };

  slideEnd = () => {
    const slider = document.querySelector('.slider-wrap');
    slider.classList.remove('active');
  };

  resetProgressBar = () => {
    const { progressBarWidth } = this.state;
    this.canvas.getContext('2d').clearRect(0, 0, progressBarWidth, 5);
    this.canvas.getContext('2d').fillStyle = 'rgba(216, 216, 216, .5)';
    this.canvas.getContext('2d').fillRect(0, 0, progressBarWidth, 5);
  };

  setVolumeIcon = () => {
    const { volumeLevel, playing } = this.state;
    if (volumeLevel === 0 && playing)
      return (
        <Icon>volume_mute</Icon>
      );

    if (!playing)
      return (
        <Icon>volume_off</Icon>
      );

    return volumeLevel <= 0.5 ? (
      <Icon>volume_down</Icon>
    ) : (
      <Icon>volume_up</Icon>
    );
  };

  handleTimeUpdate = () => {
    this.setState({ currentTime: this.audio.currentTime });

    if (this.audio.currentTime >= this.audio.duration) {
      this.setState({ currentTime: 0, playing: false });
    }
  }

  render() {
    const { playing,
      volumeLevel,
      progressBarWidth,
      currentTime,
    } = this.state;

    const {
      playerStyles,
      tracks,
      showTranscript,
      toggleTranscript,
      readMore,
      t,
    } = this.props;

    const volumeIcon = this.setVolumeIcon();
    const formattedCurrentTime = this.formatTime(currentTime);

    // TB-712: https://concept3d.atlassian.net/browse/TB-712
    // BUG CASE: remove height from #audio-player when transcript is displayed
    let audioPlayerWrapperClasses = '';

    if (readMore && showTranscript) {
      audioPlayerWrapperClasses = 'hide transcript-shown';
    } else if (readMore) {
      audioPlayerWrapperClasses = 'hide';
    } else if (showTranscript) {
      audioPlayerWrapperClasses = 'transcript-shown';
    } else {
      audioPlayerWrapperClasses = '';
    }

    return (
      <React.Fragment>
        <div
          id="audio-player"
          className={audioPlayerWrapperClasses}
          style={playerStyles}
          aria-label={t('publicTour.ada.audioPlayer.controls')}
        >
          <Button
            className="audio-ctrl"
            handleClick={this.togglePlay}
            aria-label={t(
              `publicTour.ada.audioPlayer.${playing ? 'pause' : 'play'}`
            )}
            data-cy="audio-ctrl-play-btn"
          >
            <Icon>{playing ? 'pause' : 'play_arrow'}</Icon>
          </Button>
          <span className="audio__current-time">{formattedCurrentTime}</span>
          <canvas
            aria-hidden="true"
            id="progress-bar"
            width={progressBarWidth}
            height={5}
            style={{ borderRadius: 5 }}
          />
          <audio
            id="audio"
            src={`https://res.cloudinary.com/concept3d/video/upload/vc_auto/audio_files/${tracks[0].id}`}
            onTimeUpdate={this.updateBar}
          />
          <Button
            myRef={node => (this.audioCtrl = node)}
            className="audio-player-icon audio-ctrl"
            handleClick={this.toggleMute}
            tabIndex={0}
            data-cy="audio-ctrl-mute-btn"
            aria-label={`${volumeLevel
                ? `${t(`publicTour.ada.audioPlayer.volume`)} ${Math.floor(
                  volumeLevel * 100
                )}%`
                : t(`publicTour.ada.audioPlayer.muted`)
              }`}
          >
            {volumeIcon}
            <div className="slider-wrap">
              <Slider
                orientation="vertical"
                min={0}
                max={1}
                step={0.001}
                value={volumeLevel}
                tooltip={false}
                onChange={this.changeVolume}
                onChangeStart={this.slideStart}
                onChangeComplete={this.slideEnd}
                onSliderClick={this.changeVolume}
              />
            </div>
          </Button>
          {tracks && tracks[0].transcript && (
            <>
              <Button
                className="audio-player-icon audio-ctrl transcript-btn"
                handleClick={toggleTranscript}
                aria-label={t(`publicTour.ada.audioPlayer.toggleTranscript`)}
              >
                <Icon>description</Icon>
              </Button>
            </>
          )}
        </div>
      </React.Fragment>
    );
  }
}

export default withTranslation()(AudioPlayer);
